import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AeronetViewProps } from '../types';
// import AeronetDataGrid from '../components/datagrid/DataGrid';
import {
  GridColDef,
  GridSortItem,
  getGridNumericOperators,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';
import { useCurrentUser } from '../hooks/useCurrentUser';
import AeronetDataGrid, { QueryResults } from '../components/datagrid/DataGrid';
import { AxiosResponse } from 'axios';
import { axios } from '../api';
import queryString from 'query-string';
import { InspectionDeletePayload, InspectionList } from './types';
import { useSnackbar } from 'notistack';
import { CacheRequestConfig } from 'axios-cache-interceptor';
import { useNavigate } from 'react-router-dom';
import { ActionMenuItem } from '../components/datagrid/ActionMenu';
import AlertDialog from '../components/AlertDialog';
import { Button } from '@mui/material';
import { tenantDateFilterOperators } from '../components/datagrid/tenantDateFilter';
import { CREW_FILTER_OPERATORS, splitCrewFilter } from '../flight-duty/components/crewFilter';
import { useAircraftOptions } from '../hooks/useAircraftOptions';
import { useConfig } from '../hooks/useConfig';
import { useTypes } from '../hooks/useTypes';
import { getAircraftConfigurations } from '../aircraft-configuration/aircraft_configuration';
import { loadAtaChapterOptions } from '../ata-chapters/AtaChapterAutocomplete';
import { ataChapterLabel } from '../ata-chapters/types';

// FILTERS (Aaron 2026-10-09, "/app/inspections needs filters - check other
// modules"). Legacy's nav search, mod_aircraft_templates/includes/
// aircraft_templates_nav.inc:95-196, filters by ID / Reference / Inspection,
// MODEL, AIRCRAFT and TYPE. Here: ID / Inspection / Reference / Version Date /
// Inspection Type are the grid's own column filters, which GET /v6/inspection
// applies as filter specs; Model and Aircraft are not columns of
// aircraft_inspections, so they are hidden `is`-only columns lifted out of
// `params.filter` into the route's named `model_id` / `aircraft_id` (and the
// version-25 ATA Chapter, `ata_chapter_id`, the same way)
// (Production's Cell / In progress shape, PATTERNS.md BP-95 / BP-96). Every
// operator offered below is one the route applies —
// app/inspections/test/test_list_filters.py walks the same set.
const only = <T extends { value: string }>(ops: T[], keep: string[]) =>
  ops.filter((op) => keep.includes(op.value));
const ID_FILTER_OPS = only(getGridNumericOperators(), ["=", "!=", ">", ">=", "<", "<="]);
const TEXT_FILTER_OPS = only(getGridStringOperators(), ["contains", "equals", "startsWith", "endsWith"]);
export const AIRCRAFT_FILTER_FIELD = "aircraft_id";
export const MODEL_FILTER_FIELD = "model_id";
// version-25: the inspection's own ATA chapter (aircraft_inspections.ata_chapter_id).
// Deferred + guarded server-side, so it rides as the named `ata_chapter_id` too.
export const ATA_CHAPTER_FILTER_FIELD = "ata_chapter_id";

interface InspcetionDataGridProps extends AeronetViewProps{
  }

const RENAMED_FIELDS = { type_obj: "inspection_type_id" };
// Model / Aircraft are there to be filtered on — the row carries neither.
const INITIAL_STATE = {
  columns: {
    columnVisibilityModel: {
      [MODEL_FILTER_FIELD]: false,
      [AIRCRAFT_FILTER_FIELD]: false,
      // Shown from Display: unlike Model / Aircraft, the row carries it.
      [ATA_CHAPTER_FILTER_FIELD]: false,
    },
  },
};

const InspcetionDataGrid: React.FC<InspcetionDataGridProps> = (props) => {
  const navigate = useNavigate()
  const currentUser = useCurrentUser();
  const {enqueueSnackbar} = useSnackbar();
  const [tabDialogOpen, setTabDialogOpen] = useState(false);
  const [deletePayload, setDeletePayload] = useState<InspectionDeletePayload | null>(
    null
  );
 
  const [selectedRows, setSelectedRows] = useState<InspectionList[]>([]);
  // Option lists for the singleSelect filters. The inspection-type vocabulary
  // is the `types` subtree under aircraft_configuration.inspection_type_parent
  // (as AircraftInspectionForm reads it); config values arrive as strings.
  const aircraftConfiguration = useConfig('aircraft_configuration') as any;
  const typeParentId = aircraftConfiguration?.inspection_type_parent
    ? Number(aircraftConfiguration.inspection_type_parent)
    : undefined;
  const inspectionTypes = useTypes(typeParentId);
  const aircraftOptions = useAircraftOptions();
  const [modelOptions, setModelOptions] = useState<{ value: number; label: string }[]>([]);
  const [ataChapterOptions, setAtaChapterOptions] = useState<{ value: number; label: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    loadAtaChapterOptions()
      .then((chapters) => {
        if (!cancelled) setAtaChapterOptions(chapters.map((c) => ({ value: c.id, label: ataChapterLabel(c) })));
      })
      .catch(() => {
        /* honest empty: the ATA Chapter filter offers nothing it could not read */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    // Legacy's MODEL list: active configurations by model
    // (aircraft_templates_nav.inc:125-131) — the route's own default.
    getAircraftConfigurations({ page: 1, size: 100 })
      .then(({ rows }) => {
        if (cancelled) return;
        setModelOptions(
          rows
            .filter((r) => r.model)
            .map((r) => ({ value: r.id, label: String(r.model) }))
        );
      })
      .catch(() => {
        /* honest empty: the Model filter offers nothing it could not read */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns: GridColDef[] = useMemo(()=> [
    {
      headerName:"ID",
      field:"id",
      flex:1,
      filterOperators: ID_FILTER_OPS,
    },
    {
      headerName:"Inspection",
      field:"title",
      flex:1,
      filterOperators: TEXT_FILTER_OPS,
    },
    {
      headerName:"Reference",
      field:"reference",
      flex:1,
      filterOperators: TEXT_FILTER_OPS,
    },
    {
      headerName:"Version Date",
      field:"version_date",
      flex:1,
      // The wire's YYYY-MM-DD, as served and as the tenant picker writes it.
      filterOperators: tenantDateFilterOperators(),
    },
    {
      // The list route serves the joined inspection type as `type_obj`
      // ({id,name}) — there is NO `type` key on the payload. Reading `type`
      // rendered this column permanently blank AND 422'd the entire grid on a
      // header click: app/sorting.py passes an unknown field straight through
      // to apply_sort, which raises FieldNotFound -> RequestValidationError
      // -> 422 (app/crud.py:73).
      //
      // The field is the real column `inspection_type_id` so the FILTERS item
      // reaches the route as a spec (`is` only: one type). Was `type_obj`;
      // `renamedFields` below keeps a saved layout's place for it.
      //
      // sortable:false: sorting the id would order by number, not by name, and
      // `type_obj` resolves to a nested alias dict app/sorting.py skips.
      headerName:"Inspection Type",
      field:"inspection_type_id",
      flex:1,
      sortable: false,
      type: "singleSelect",
      valueOptions: (inspectionTypes ?? []).map((t) => ({ value: t.id, label: t.name })),
      filterOperators: CREW_FILTER_OPERATORS,
      valueGetter: (params) => params.row?.type_obj?.id ?? "",
      renderCell: (params) => params.row?.type_obj?.name ?? "",
      // The NAME in an export, not the id the filter needs.
      valueFormatter: (params) =>
        params.id != null ? params.api.getRow(params.id)?.type_obj?.name ?? "" : "",
    },
    {
      // Legacy "MODEL" — a FILTERS item, hidden, sent as model_id.
      headerName:"Model",
      field: MODEL_FILTER_FIELD,
      flex:1,
      sortable: false,
      hideable: false,
      disableExport: true,
      type: "singleSelect",
      valueOptions: modelOptions,
      filterOperators: CREW_FILTER_OPERATORS,
      valueGetter: () => "",
    },
    {
      // Legacy "AIRCRAFT" — a FILTERS item, hidden, sent as aircraft_id.
      headerName:"Aircraft",
      field: AIRCRAFT_FILTER_FIELD,
      flex:1,
      sortable: false,
      hideable: false,
      disableExport: true,
      type: "singleSelect",
      valueOptions: aircraftOptions.map((a) => ({ value: a.id, label: a.registration })),
      filterOperators: CREW_FILTER_OPERATORS,
      valueGetter: () => "",
    },
    {
      // The inspection's ATA chapter (version-25), served on the row as
      // `ata_chapter` {id, code, title}; null = none, or the tenant has no column.
      headerName:"ATA Chapter",
      field: ATA_CHAPTER_FILTER_FIELD,
      flex:1,
      sortable: false,
      type: "singleSelect",
      valueOptions: ataChapterOptions,
      filterOperators: CREW_FILTER_OPERATORS,
      valueGetter: (params) => params.row?.ata_chapter?.id ?? "",
      renderCell: (params) => ataChapterLabel(params.row?.ata_chapter),
      valueFormatter: (params) =>
        params.id != null ? ataChapterLabel(params.api.getRow(params.id)?.ata_chapter) : "",
    },
  ],[inspectionTypes, modelOptions, aircraftOptions, ataChapterOptions])

  // Legacy's quick search "for" ID / Reference / Inspection
  // (aircraft_templates_nav.inc:95-116); InspectionsTRList's set.
  const quickSearchItems = useMemo(
    () => [
      { field: "title", operator: "contains", label: "Inspection" },
      { field: "reference", operator: "contains", label: "Reference" },
      // `==` not `contains` — `id` is an INT column.
      { field: "id", operator: "==", label: "ID" },
    ],
    []
  );

  const getData = useCallback(
    async (params: any, axiosConfig?: CacheRequestConfig) => {
      try {
        // Lift Model / Aircraft out of the filter specs into the route's
        // named params — removed either way, so never applied twice.
        const model = splitCrewFilter(params.filter, MODEL_FILTER_FIELD);
        const aircraft = splitCrewFilter(model.rest, AIRCRAFT_FILTER_FIELD);
        const chapter = splitCrewFilter(aircraft.rest, ATA_CHAPTER_FILTER_FIELD);
        const query: Record<string, unknown> = { ...params };
        delete query.filter;
        if (chapter.rest.length > 0) query.filter = chapter.rest;
        if (model.uid != null) query.model_id = model.uid;
        if (aircraft.uid != null) query.aircraft_id = aircraft.uid;
        if (chapter.uid != null) query.ata_chapter_id = chapter.uid;
        const resp: AxiosResponse =
          await axios.get("/v6/inspection", {
            params: query,
            paramsSerializer: (params) => queryString.stringify(params),
            ...axiosConfig,
          });
          
        return {
          total: resp.data.total,
          rows: resp.data.data,
        } as QueryResults<InspectionList>;
      } catch (e) {
        // @ts-ignore
        if (!axios.isCancel(e)) {
          enqueueSnackbar("Error fetching inspections", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        }
        throw e;
      }
    },
    [enqueueSnackbar])

    const deleteSors = useCallback(() => {
      const inspectionIds = selectedRows.map((row) => row.id);
      if (!inspectionIds || inspectionIds.length === 0) {
        return;
      }
      for (const id of inspectionIds) {
        axios
          .delete(`/v6/inspection/${id}`)
          .then(() => {
            enqueueSnackbar(`Inspection deleted ${id}`, {
              variant: "success",
            });
            if (deletePayload?.apiRef) {
              deletePayload.apiRef.current.updateRows([
                { id: id, _action: "delete" },
              ]);
            }
          })
          .catch((error) => {
            console.error(error);
            enqueueSnackbar(`Error deleting Inspection`, {
              variant: "error",
            });
          });
      }
      deletePayload?.setSelectionModel([]);
      setDeletePayload(null);
      setSelectedRows([]);
    }, [deletePayload, enqueueSnackbar, selectedRows]);

    const actions:ActionMenuItem[] = useMemo(()=>{
      const defaultActions : ActionMenuItem[] = [
        {
          name: "Delete",
          namePlural: "Delete",
          action: ["Delete", "DELETE"],
          requiresSelection: true,
          multipleSelection: true,
          onClick: async (event: InspectionDeletePayload) => {
            const rows = [...event.selectedRows].map((row) => row);
  
            setTabDialogOpen(true);
            setDeletePayload(event);
            setSelectedRows(rows);
          },
        }
      ]
      return defaultActions
    },[])

    const onRowClick = useCallback(
      (params, event) => {
        event.stopPropagation();
  
        let currentElement = event.target;
  
        // Traverse up the DOM tree to check if any parent element has the 'pdf-icon-button' class
        while (currentElement) {
          // Ensure className is a string before calling includes
          if (
            typeof currentElement.className === "string" &&
            currentElement.className.includes("pdf-icon-button")
          ) {
            // If it's part of the PDF icon button, do not navigate
            return;
          }
          currentElement = currentElement.parentElement;
        }
  
        // If the clicked element is not part of the PDF icon, navigate
        navigate(String(params.id));
      },
      [navigate]
    );
  const initialSort: GridSortItem[] = useMemo(
    () => [{ field: "id", sort: "desc" }],
    []
  );

  return (
    <>
     <AeronetDataGrid
     columns={columns}
     actions={actions}
    data={getData}
    initialSort={initialSort}
    enableAdvancedSearch={false}
    enableFilter={true}
    disableColumnFilter={false}
    quickSearchItems={quickSearchItems}
    renamedFields={RENAMED_FIELDS}
    initialState={INITIAL_STATE}
     name='Inspection-Data-Grid'
     currentUserId={currentUser?.id}
     onRowClick={onRowClick}
     />
      <AlertDialog
        title="Delete this record?"
        open={tabDialogOpen}
        handleClose={() => {
          setTabDialogOpen(false);
        }}
      >
        <Button
          onClick={() => {
            setTabDialogOpen(false);
          }}
          color="primary"
        >
          No
        </Button>
        <Button
          onClick={() => {
            setTabDialogOpen(false);
            deleteSors();
          }}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
      </AlertDialog>
    </>
  )
}

export default InspcetionDataGrid